const root = document.documentElement;
const PROFILE_AUTO_DELAY = 2500;
const PROFILE_COOLDOWN_DAYS = 30;
const PROFILE_COOLDOWN_MS = PROFILE_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

const emit = (name, detail = {}) => {
  document.dispatchEvent(new CustomEvent(name, { detail }));
  if (window.Shopify?.analytics?.publish) window.Shopify.analytics.publish(name, detail);
};

const safeStorage = {
  get(storage, key, fallback = null) {
    try {
      const value = storage.getItem(key);
      return value === null ? fallback : value;
    } catch (error) {
      return fallback;
    }
  },
  set(storage, key, value) {
    try {
      storage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  },
  remove(storage, key) {
    try {
      storage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  },
  json(storage, key, fallback = {}) {
    try {
      return JSON.parse(this.get(storage, key, '') || JSON.stringify(fallback));
    } catch (error) {
      return fallback;
    }
  }
};

class PetProfile {
  constructor(dialog) {
    this.dialog = dialog;
    this.steps = [...dialog.querySelectorAll('[data-profile-step]')];
    this.data = safeStorage.json(window.localStorage, 'playkind.pet', {});
    this.index = Number.isInteger(this.data.step) ? Math.min(this.data.step, this.steps.length - 1) : 0;
    this.nameTargets = [...dialog.querySelectorAll('[data-pet-name]')];
    this.avatarNames = [
      'cavoodle', 'frenchie', 'golden', 'collie',
      'groodle', 'dachshund', 'shepherd', 'labrador',
      'maltese', 'poodle', 'whippet', 'staffy',
      'doberman', 'kelpie', 'corgi', 'rescue'
    ];
    this.returnFocus = null;
    this.bind();
    this.restore();
    this.render();
    this.scheduleAutomaticOpen();
  }

  bind() {
    document.querySelectorAll('[data-open-profile]').forEach(button => {
      button.addEventListener('click', event => {
        event.preventDefault();
        this.open(button, 'manual');
      });
    });
    this.dialog.querySelectorAll('[data-close-profile]').forEach(button => button.addEventListener('click', () => this.close(true)));
    this.dialog.querySelector('[data-profile-back]')?.addEventListener('click', () => this.back());
    this.dialog.querySelector('[data-profile-delete]')?.addEventListener('click', () => this.remove());
    this.dialog.querySelectorAll('[data-avatar-choice]').forEach(button => button.addEventListener('click', () => this.chooseAvatar(button)));
    this.dialog.querySelectorAll('[data-answer]').forEach(button => button.addEventListener('click', () => this.answer(button)));
    this.dialog.querySelector('[data-name-form]')?.addEventListener('submit', event => {
      event.preventDefault();
      const value = String(new FormData(event.currentTarget).get('pet_name') || '').trim();
      const avatarError = this.dialog.querySelector('[data-avatar-error]');
      if (!this.data.avatar) {
        if (avatarError) avatarError.hidden = false;
        this.dialog.querySelector('[data-avatar-choice]')?.focus();
        return;
      }
      if (value) {
        if (avatarError) avatarError.hidden = true;
        this.data.name = value;
        this.next();
      }
    });
    this.dialog.addEventListener('cancel', event => {
      event.preventDefault();
      this.close(true);
    });
    this.dialog.addEventListener('click', event => {
      if (event.target === this.dialog) this.close(true);
    });
  }

  chooseAvatar(button) {
    this.data.avatar = button.dataset.avatar;
    this.dialog.querySelectorAll('[data-avatar-choice]').forEach(choice => choice.setAttribute('aria-pressed', String(choice === button)));
    const avatarError = this.dialog.querySelector('[data-avatar-error]');
    if (avatarError) avatarError.hidden = true;
    this.updateAvatarSurfaces();
  }

  updateAvatarSurfaces() {
    const avatar = this.avatarNames.includes(this.data.avatar) ? this.data.avatar : 'cavoodle';
    document.querySelectorAll('[data-profile-avatar], [data-profile-dock-avatar]').forEach(surface => {
      this.avatarNames.forEach(name => surface.classList.remove(`avatar--${name}`));
      surface.classList.add(`avatar--${avatar}`);
    });
  }

  scheduleAutomaticOpen() {
    if (!document.body.classList.contains('template-index')) return;
    if (this.data.completed) return;
    const dismissedAt = Number(safeStorage.get(window.localStorage, 'playkind.profile.dismissedAt', '0'));
    if (dismissedAt && Date.now() - dismissedAt < PROFILE_COOLDOWN_MS) return;
    window.setTimeout(() => {
      if (!this.isOpen()) this.open(document.querySelector('[data-open-profile]'), 'automatic');
    }, PROFILE_AUTO_DELAY);
  }

  isOpen() {
    return this.dialog.open || this.dialog.hasAttribute('open');
  }

  open(trigger, source = 'manual') {
    this.returnFocus = trigger || document.activeElement;
    if (typeof this.dialog.showModal === 'function') {
      if (!this.dialog.open) this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
      this.dialog.setAttribute('role', 'dialog');
      this.dialog.setAttribute('aria-modal', 'true');
      this.dialog.classList.add('is-dialog-fallback');
    }
    document.body.classList.add('no-scroll');
    window.requestAnimationFrame(() => this.dialog.querySelector('input, button, a')?.focus());
    emit('pet_profile_opened', { source });
  }

  close(rememberDismissal = false) {
    if (rememberDismissal) safeStorage.set(window.localStorage, 'playkind.profile.dismissedAt', String(Date.now()));
    if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
    this.dialog.removeAttribute('open');
    this.dialog.classList.remove('is-dialog-fallback');
    document.body.classList.remove('no-scroll');
    this.returnFocus?.focus?.();
  }

  answer(button) {
    this.data[button.dataset.field] = button.dataset.answer;
    this.dialog.querySelectorAll(`[data-field="${button.dataset.field}"]`).forEach(option => option.setAttribute('aria-pressed', String(option === button)));
    this.next();
  }

  next() {
    if (this.index === this.steps.length - 2) {
      this.complete();
      return;
    }
    if (this.index < this.steps.length - 1) {
      this.index += 1;
      this.save();
      this.render();
    }
  }

  back() {
    if (this.index > 0) {
      this.index -= 1;
      this.save();
      this.render();
    }
  }

  save() {
    this.data.step = this.index;
    safeStorage.set(window.localStorage, 'playkind.pet', JSON.stringify(this.data));
  }

  restore() {
    if (this.data.name) {
      this.nameTargets.forEach(target => { target.textContent = this.data.name; });
      document.querySelectorAll('[data-profile-label]').forEach(target => { target.textContent = `${this.data.name}'s profile`; });
      document.querySelectorAll('[data-profile-dock-kicker]').forEach(target => { target.textContent = 'Saved play profile'; });
      const input = this.dialog.querySelector('[name="pet_name"]');
      if (input) input.value = this.data.name;
    }
    if (this.data.avatar) this.dialog.querySelector(`[data-avatar="${this.data.avatar}"]`)?.setAttribute('aria-pressed', 'true');
    this.updateAvatarSurfaces();
    Object.entries(this.data).forEach(([field, value]) => {
      this.dialog.querySelector(`[data-field="${field}"][data-answer="${value}"]`)?.setAttribute('aria-pressed', 'true');
    });
  }

  render() {
    this.steps.forEach((step, position) => step.classList.toggle('is-active', position === this.index));
    this.dialog.scrollTop = 0;
    const questionCount = this.steps.length - 1;
    this.dialog.style.setProperty('--progress', `${(Math.min(this.index + 1, questionCount) / questionCount) * 100}%`);
    this.nameTargets.forEach(target => { target.textContent = this.data.name || 'your dog'; });
    const back = this.dialog.querySelector('[data-profile-back]');
    if (back) back.disabled = this.index === 0;
  }

  complete() {
    this.data.completed = true;
    this.index = this.steps.length - 1;
    this.save();
    const personality = {
      calm: 'a calm companion', energetic: 'an energetic playmate', curious: 'a curious explorer',
      bold: 'a strong-minded problem solver', affectionate: 'an affectionate sidekick'
    }[this.data.personality] || 'ready for a better kind of play';
    const priorities = {
      durability: ['Built for repeat play', 'Start with sturdy forms and clear supervision guidance.'],
      design: ['Good-looking play objects', 'Start with expressive colour and forms you will enjoy leaving at home.'],
      engagement: ['Interest that lasts', 'Start with toys that change how the game unfolds.'],
      cleaning: ['Easy-care favourites', 'Start with simple materials and a realistic cleaning routine.'],
      quiet: ['Calmer home play', 'Start with lower-noise choices for shared spaces.']
    };
    const secondary = {
      calm: 'Gentle carrying and slower discovery suit their easy rhythm.',
      energetic: 'Movement and short challenges help turn energy into focused play.',
      curious: 'A toy with more than one way in keeps their attention moving.',
      bold: 'Clear challenges and repeatable wins suit a confident problem solver.',
      affectionate: 'Shared play and carry-along comfort fit a close companion.'
    };
    const routines = {
      active: 'Choose something you can throw, tug or move together.',
      short: 'Look for a toy that feels rewarding in a five-minute play break.',
      independent: 'Prioritise safe setup and satisfying self-directed discovery.',
      mixed: 'Keep one independent mode and one easy shared game in rotation.'
    };
    const traitLabels = {
      size: { small: 'Small', medium: 'Medium', large: 'Large', unsure: 'To confirm' },
      personality: { calm: 'Easy-going', energetic: 'Energetic', curious: 'Curious', bold: 'Strong-minded', affectionate: 'Affectionate' },
      owner_mode: { active: 'Active together', short: 'Quick sessions', independent: 'Independent play', mixed: 'A flexible mix' },
      value: { durability: 'Longer-lasting', design: 'Design-led', engagement: 'Keeps interest', cleaning: 'Easy care', quiet: 'Quieter play' }
    };
    const summary = this.dialog.querySelector('[data-profile-summary]');
    if (summary) summary.textContent = `${this.data.name || 'Your dog'} is ${personality}. Their best matches will balance ${traitLabels.value[this.data.value]?.toLowerCase() || 'engaging play'} with the way you spend time together.`;
    this.dialog.querySelectorAll('[data-profile-trait]').forEach(target => {
      const field = target.dataset.profileTrait;
      target.textContent = traitLabels[field]?.[this.data[field]] || '—';
    });
    const signature = [...`${this.data.name || 'DOG'}${this.data.avatar || ''}`].reduce((total, character) => total + character.charCodeAt(0), 0);
    const profileId = this.dialog.querySelector('[data-profile-id]');
    if (profileId) profileId.textContent = `PK–${String((signature * 37) % 10000).padStart(4, '0')}`;
    const recommendation = priorities[this.data.value] || ['A thoughtful first match', 'Start with a versatile toy that is easy to understand.'];
    const recommendationTitle = this.dialog.querySelector('[data-recommendation-title]');
    const recommendationReason = this.dialog.querySelector('[data-recommendation-reason]');
    const recommendationSecondary = this.dialog.querySelector('[data-recommendation-secondary]');
    const recommendationRoutine = this.dialog.querySelector('[data-recommendation-routine]');
    if (recommendationTitle) recommendationTitle.textContent = recommendation[0];
    if (recommendationReason) recommendationReason.textContent = recommendation[1];
    if (recommendationSecondary) recommendationSecondary.textContent = secondary[this.data.personality] || 'Try a second texture or movement to keep their choices interesting.';
    if (recommendationRoutine) recommendationRoutine.textContent = routines[this.data.owner_mode] || 'Choose a play pattern that fits naturally into the day.';
    const params = new URLSearchParams();
    if (this.data.size && this.data.size !== 'unsure') params.set('filter.p.m.pet.size', this.data.size);
    const rootUrl = window.Shopify?.routes?.root || '/';
    this.dialog.querySelectorAll('[data-recommendation-link]').forEach(link => { link.href = `${rootUrl}collections/all?${params}`; });
    this.restore();
    this.render();
    this.dialog.classList.add('is-completing');
    const toast = document.querySelector('[data-profile-ready-toast]');
    const toastName = document.querySelector('[data-profile-toast-name]');
    if (toastName) toastName.textContent = this.data.name || 'Your dog';
    window.setTimeout(() => {
      this.close(false);
      this.dialog.classList.remove('is-completing');
      document.body.classList.add('profile-avatar-arrival');
      toast?.classList.add('is-visible');
      window.setTimeout(() => toast?.classList.remove('is-visible'), 3200);
      window.setTimeout(() => document.body.classList.remove('profile-avatar-arrival'), 1500);
    }, 1250);
    emit('pet_profile_completed', {
      size: this.data.size, personality: this.data.personality, owner_mode: this.data.owner_mode,
      value: this.data.value, avatar: this.data.avatar
    });
  }

  remove() {
    safeStorage.remove(window.localStorage, 'playkind.pet');
    safeStorage.remove(window.localStorage, 'playkind.profile.dismissedAt');
    this.data = {};
    this.index = 0;
    this.dialog.querySelector('[data-name-form]')?.reset();
    this.dialog.querySelectorAll('[data-avatar-choice]').forEach(button => button.setAttribute('aria-pressed', 'false'));
    this.dialog.querySelectorAll('[data-answer]').forEach(button => button.setAttribute('aria-pressed', 'false'));
    this.render();
    document.querySelectorAll('[data-profile-label]').forEach(target => { target.textContent = 'Find my dog’s match'; });
    document.querySelectorAll('[data-profile-dock-kicker]').forEach(target => { target.textContent = 'Play profile'; });
    this.updateAvatarSurfaces();
    emit('pet_profile_deleted');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const dialog = document.querySelector('[data-profile-dialog]');
  if (dialog) new PetProfile(dialog);

  document.querySelectorAll('[data-size-range]').forEach(range => {
    const update = () => {
      const index = Number(range.value);
      const sizes = [95, 145, 205];
      const labels = ['About a tennis ball', 'About an adult hand', 'About a 330 ml drink can'];
      const area = range.closest('[data-size-visual]');
      if (!area) return;
      area.style.setProperty('--shape-size', `${sizes[index]}px`);
      const label = area.querySelector('[data-comparison-label]');
      if (label) label.textContent = labels[index];
      emit('size_help_used', { size: ['small', 'medium', 'large'][index] });
    };
    range.addEventListener('input', update);
    update();
  });

  document.querySelectorAll('[data-sound-button]').forEach(button => button.addEventListener('click', () => {
    const audio = document.getElementById(button.getAttribute('aria-controls'));
    if (!audio) return;
    audio.currentTime = 0;
    audio.play();
    emit('sound_sample_played');
  }));

  document.querySelectorAll('[data-mobile-facets]').forEach(button => button.addEventListener('click', () => {
    const panel = document.querySelector('.facet-panel');
    if (!panel) return;
    panel.classList.toggle('is-open');
    button.setAttribute('aria-expanded', String(panel.classList.contains('is-open')));
  }));

  document.querySelectorAll('[data-sort-collection]').forEach(select => select.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    window.location.assign(url.toString());
  }));

  document.querySelectorAll('[data-variant-select]').forEach(select => select.addEventListener('change', () => {
    const selected = select.selectedOptions[0];
    const form = select.closest('form');
    const productInfo = select.closest('.product-info');
    const price = productInfo?.querySelector('[data-product-price]');
    const inventory = productInfo?.querySelector('[data-product-inventory]');
    const submit = form?.querySelector('[type="submit"]');
    const stickyPrice = document.querySelector('[data-sticky-price]');
    const stickySubmit = document.querySelector('[data-submit-product]');
    if (price && selected?.dataset.price) price.textContent = selected.dataset.price;
    if (stickyPrice && selected?.dataset.price) stickyPrice.textContent = selected.dataset.price;
    if (inventory) inventory.textContent = selected?.dataset.available === 'true' ? inventory.dataset.inStock : inventory.dataset.soldOut;
    if (submit) submit.disabled = selected?.dataset.available !== 'true';
    if (stickySubmit) stickySubmit.disabled = selected?.dataset.available !== 'true';
  }));

  document.querySelectorAll('[data-sticky-buy]').forEach(sticky => {
    const form = document.querySelector('[data-ajax-product-form]');
    const submit = form?.querySelector('[type="submit"]');
    sticky.querySelector('[data-submit-product]')?.addEventListener('click', () => submit?.click());
    if (!form || !('IntersectionObserver' in window)) {
      sticky.classList.add('is-visible');
      return;
    }
    const observer = new IntersectionObserver(entries => {
      sticky.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { threshold: 0.15 });
    observer.observe(form);
  });

  document.querySelectorAll('[data-ajax-product-form]').forEach(form => form.addEventListener('submit', async event => {
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (!button) return;
    button.disabled = true;
    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootUrl}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) throw new Error('Unable to add product');
      await response.json();
      const liveRegion = document.getElementById('cart-live-region');
      if (liveRegion) liveRegion.textContent = 'Added to cart';
      emit('product_added', { product_id: form.dataset.productId });
      window.location.href = `${rootUrl}cart`;
    } catch (error) {
      const message = form.querySelector('[data-product-error]');
      if (message) message.textContent = 'This item could not be added. Please refresh and try again.';
      button.disabled = false;
    }
  }));
});
